import { Injectable } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { MatAccordion } from '@angular/material/expansion';
import { SplugDefinitionRemoteControllerService } from 'core/api';
import { ISplugValidationDto, IUserDto } from 'core/dto';
import { mapKey, subjectFromObs } from 'core/rx-js';
import { LoaderService } from 'core/services';
import { SplugConfigService } from 'core/splug';
import { DsgDeepTranslationKey } from 'projects/designer/src/app/pipes/translate.pipe';
import { BehaviorSubject, Subject, combineLatest, distinctUntilChanged, filter, map, of, switchMap, take, tap } from 'rxjs';
import { RemoteUserService } from '../remotes/remote-user.service';
import { UserDetailService } from '../user-detail.service';

type ArrayInferer<T extends any[]> = T extends Array<infer U> ? U : never

@Injectable()
export class UserSplugConfigService {

  private readonly _destroy = new Subject<void>()

  readonly accordionSubject = new BehaviorSubject<MatAccordion>(undefined);
  readonly splugFormArray = new FormArray<any>([]);

  readonly formsValidObs = this.splugFormArray.statusChanges.pipe(
    map(status => status === 'VALID'),
    distinctUntilChanged()
  )

  readonly validationResultSubject = new BehaviorSubject<{ [splugId: string]: ISplugValidationDto }>({});

  readonly splugDefinitionsSubject = this._splugConfigService.splugDefinitionsSubject;
  readonly validSplugsObs = this.validationResultSubject.pipe(
    filter(Boolean),
    map((validationResult) => Object.entries(validationResult)
      .reduce<{ [splugId: string]: boolean }>((total, [splugId, validationResult]) => ({ ...total, [splugId]: validationResult.status === 'OK' }),
        {}
      )
    )
  )

  readonly initalSplugFormDataSubject = subjectFromObs({
    initialValue: null,
    observable: this._remoteUserService.userSubject.pipe(
      filter(Boolean),
      map(user => user.sigConfigs?.reduce<{ [key: string]: any }>((total, current) => {
        total[current.sigTypeId] = current.params;
        return total
      }, {})
      )),
  })

  readonly splugFormGroup: { [splug: string]: FormGroup } = {}

  readonly selectedSplugsSubject = new BehaviorSubject<{ [splugId: string]: boolean }>({})
  get selectedSplugs() {
    return this.selectedSplugsSubject.value;
  }
  get userId() {
    return '';
  }

  readonly splugParametersConfigurationsSubject = subjectFromObs({
    observable: this._userDetailService.userSubject.pipe(filter(Boolean), mapKey('sigConfigs')),
    initialValue: null
  })

  constructor(
    private readonly _splugDefinitionRemoteControllerService: SplugDefinitionRemoteControllerService,
    private readonly _splugConfigService: SplugConfigService,
    private readonly _remoteUserService: RemoteUserService,
    private readonly _userDetailService: UserDetailService,
    private readonly _loaderService: LoaderService<DsgDeepTranslationKey>
  ) {


  }

  validateAndSave() {

    const id = this._loaderService.show({ key: 'user-splug.validate', text: 'ss-entities.user.remote-actions.validate-splugs' })

    return this.validate()
      .pipe(
        take(1),
        this._loaderService.disposeLoader(id),
        filter((validationResult) => validationResult?.isValid),
        switchMap(({ actorSplugsWithParams }) => {

          const id = this._loaderService.show({ key: 'user-splug.save', text: 'ss-entities.user.remote-actions.save-splugs' });
          return this.#save(actorSplugsWithParams)
            .pipe(this._loaderService.disposeLoader(id))

        }),

      )

  }

  #save(userSplugWithParams: IUserDto['sigConfigs']) {

    this.validationResultSubject.next({})
    return this._userDetailService.updateUserSplugConfigurations(userSplugWithParams);
  }

  validate() {


    const formsWithData = Object.entries(this.splugFormGroup)
      .filter(([splugId, formGroup]) => {
        return this.selectedSplugs[splugId]
      })

    if (formsWithData.length === 0) {

      return of({ actorSplugsWithParams: [], isValid: true })
    }

    const id = this._loaderService.show({ key: 'user-splug.validate', text: 'ss-entities.user.remote-actions.validate-splugs' })
    return combineLatest(formsWithData.map(([splugId, form]) => {

      const keyValuePairs = Object.entries(form.value).map(([key, value]) => ({
        key,
        value: this._splugConfigService.executeStringifer(splugId, key, value)
      }))

      return this._splugDefinitionRemoteControllerService.validateSplugParams(splugId, keyValuePairs)
        .pipe(
          map((validationResult) => ({ splugId, validationResult, formData: form.value })),
        )
    }))
      .pipe(
        this._loaderService.disposeLoader(id),
        tap((validations) => {
          const validationResult = validations.reduce<{ [splugId: string]: ISplugValidationDto }>((total, { splugId, validationResult }) => {
            total[splugId] = validationResult;
            return total;
          },
            {}
          );
          this.validationResultSubject.next(validationResult);
        }),
        map((validations) => ({
          isValid: !validations.find(({ validationResult }) => validationResult.status !== 'OK'),
          actorSplugsWithParams: validations.map<ArrayInferer<IUserDto['sigConfigs']>>(({ formData, splugId }) => ({
            sigTypeId: splugId,
            params: formData,
          }))
        }))
      );

  }


  selectSplug(splugId: string) {

    this.selectedSplugsSubject.next({
      ...this.selectedSplugs,
      [splugId]: true
    })
  }

  deselectSplug(splugId: string) {

    const prev = this.selectedSplugs[splugId];

    if (!prev) {
      return;
    }

    this.selectedSplugsSubject.next({
      ...this.selectedSplugs,
      [splugId]: false
    })
  }

  reset() {
    this.initalSplugFormDataSubject.next(this.initalSplugFormDataSubject.value)
    this.validationResultSubject.next({})
  }

  ngOnDestroy() {
    this._destroy.next()
    this._destroy.complete()
  }

}